/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import {
    OrgUnitContact,
    OrgUnitLocation,
    StoreHours
} from '@msdyn365-commerce/retail-proxy';
import { buildStoreHours, IHoursDisplayInfo, secondsToTime } from './date-time-helpers';

export interface IStoreSelectorLocationLineItemResources {
    distanceUnit: string;

    contactInfoHeader: string;
    storeHoursHeader: string;
    availabilityInfoHeader: string;

    closedText: string;

    outOfStock: string;
    inStock: string;

    currentLocation: string;
    selectLocation: string;
    selectLocationAriaLabelFormat: string;

    days: {
        monday: string;
        tuesday: string;
        wednesday: string;
        thursday: string;
        friday: string;
        saturday: string;
        sunday: string;
        mondayFull: string;
        tuesdayFull: string;
        wednesdayFull: string;
        thursdayFull: string;
        fridayFull: string;
        saturdayFull: string;
        sundayFull: string;
    };
}

export interface IStoreSelectorLocationLineItemProps {
    location: OrgUnitLocation;
    storeHours?: StoreHours;

    hideStockStatus?: boolean;
    isInStock?: boolean;
    isCurrentLocation?: boolean;

    resources: IStoreSelectorLocationLineItemResources;
    stockStatusLabel?: string;

    handlers: {
        onSelected(location: OrgUnitLocation): void;
    };
}

const StoreSelectorLocationLineItemComponentActions = {
    onLocationSelected(props: IStoreSelectorLocationLineItemProps): void {
        props.handlers.onSelected(props.location);
    }
};

const renderStoreHours = (storeHours: StoreHours | undefined, location: OrgUnitLocation, resources: IStoreSelectorLocationLineItemResources): JSX.Element | null => {
    // First, use the store hours. This will allow listing day by day houts
    if (storeHours && storeHours.RegularStoreHours) {
        const storeHoursList: IHoursDisplayInfo[] = buildStoreHours(storeHours.RegularStoreHours, resources);

        return (
            <div className='ms-store-select__store-hours-details-group'>
                {storeHoursList.map(displayTime => (
                    <div className='ms-store-select__store-hours-details'>
                        {displayTime.endDayShort ? (
                            <div className='ms-store-select__store-hours-day'>
                                <abbr title={displayTime.startDayFull}>{displayTime.startDayShort}</abbr>–
                                <abbr title={displayTime.endDayFull}>{displayTime.endDayShort}</abbr>
                            </div>
                        ) : (
                                <div className='ms-store-select__store-hours-day'>
                                    <abbr title={displayTime.startDayFull}>{displayTime.startDayShort}</abbr>
                                </div>
                            )}
                        <div className='ms-store-select__store-hours-time'>{displayTime.timeRange}</div>
                    </div>
                ))}
            </div>
        );
    } else {
        // If that fails, fall back to using the OpenFrom and OpenTo properties on the location
        const storeOpenFrom: string | undefined = secondsToTime(location.OpenFrom);
        const storeOpenTo: string | undefined = secondsToTime(location.OpenTo);

        return (
            <div className='ms-store-select__store-hours-details'>
                Open {storeOpenFrom} - {storeOpenTo}
            </div>
        );
    }
};

const extractPhoneNumber = (contacts: OrgUnitContact[] | undefined): string | undefined => {
    if (!contacts) {
        return undefined;
    }

    const allPhoneContacts = contacts.filter(contact => contact.ContactTypeValue === 1 && !contact.IsPrivate);

    if (allPhoneContacts && allPhoneContacts.length > 0) {
        const primaryPhoneContact = allPhoneContacts.find(contact => contact.IsPrimary);

        if (primaryPhoneContact) {
            return primaryPhoneContact.Locator;
        }

        return allPhoneContacts[0].Locator;
    }

    return undefined;
};

const StoreSelectorLocationLineItem: React.FC<IStoreSelectorLocationLineItemProps> = (props: IStoreSelectorLocationLineItemProps) => {
    const onChange = () => {
        StoreSelectorLocationLineItemComponentActions.onLocationSelected(props);
    };

    const {
        isCurrentLocation,
        location,
        resources,
        storeHours,
    } = props;

    const storeName: string | undefined = location.OrgUnitName;
    const storeAddress: string | undefined = location.Address;
    const phoneNumber: string | undefined = extractPhoneNumber(location.Contacts);

    return (
        <label className='ms-store-select__location-line-item' htmlFor={`input-${storeName}`}>
            {/* tslint:disable-next-line: react-a11y-role-has-required-aria-props */}
            <input
                className='ms-store-select__location-line-item-input'
                id={`input-${storeName}`}
                type='radio'
                value={storeName}
                name='store-locations'
                onChange={onChange}
                defaultChecked={isCurrentLocation}
            />
            <div className='ms-store-select__location-line-item-radio-outer'>
                <div className='ms-store-select__location-line-item-radio-inner' />
            </div>
            <div className='ms-store-select__location-line-shop-details'>
                <span className='ms-store-select__location-line-item-store-name'>{storeName}</span>
                {storeAddress && (
                    <div className='ms-store-select__location-line-shop-address-section'>
                        <span className='ms-store-select__location-line-shop-address-text'>{storeAddress}</span>
                    </div>
                )}
                {phoneNumber && (
                    <div className='ms-store-select__location-line-shop-phone-section'>
                        <span className='ms-store-select__location-line-shop-phone-text'>{phoneNumber}</span>
                    </div>
                )}
                {renderStoreHours(storeHours, location, resources)}
            </div>
        </label>
    );
};

export const StoreSelectorLocationLineItemComponent = StoreSelectorLocationLineItem as (props: IStoreSelectorLocationLineItemProps) => JSX.Element;